import * as Sentry from "@sentry/react";
import React, { useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom/client";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
  });
}

import AuthGate from "./auth/AuthGate";
import AuthReset from "./auth/AuthReset";
import DocumentList from "./documents/DocumentList";
import App from "./App";
import ShareViewer from "./share/ShareViewer";
import { ModalProvider } from "./ui/Modal";
import { ToastContainer } from "./ui/Toast";
import { authClient } from "./auth/client";
import "./App.css";

interface DocInfo {
  id: string;
  name: string;
}

function Root() {
  const [activeDoc, setActiveDoc] = useState<DocInfo | null>(null);

  // On mount: if URL is /doc/:id, auto-open that document
  useEffect(() => {
    const match = window.location.pathname.match(/^\/doc\/([^/]+)$/);
    if (match) {
      const docId = match[1];
      fetch(`/api/documents/${docId}`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((doc) => {
          if (doc) setActiveDoc({ id: doc.id, name: doc.name });
        })
        .catch(() => {});
    }
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const onPop = () => {
      const match = window.location.pathname.match(/^\/doc\/([^/]+)$/);
      if (match) {
        const docId = match[1];
        fetch(`/api/documents/${docId}`, { credentials: "include" })
          .then((r) => (r.ok ? r.json() : null))
          .then((doc) => {
            if (doc) setActiveDoc({ id: doc.id, name: doc.name });
            else setActiveDoc(null);
          })
          .catch(() => setActiveDoc(null));
      } else {
        setActiveDoc(null);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const handleLogout = useCallback(async () => {
    await authClient.signOut();
    window.location.reload();
  }, []);

  const handleSelect = useCallback((doc: DocInfo) => {
    history.pushState({}, "", `/doc/${doc.id}`);
    setActiveDoc(doc);
  }, []);

  const handleBackToList = useCallback(() => {
    history.pushState({}, "", "/");
    setActiveDoc(null);
  }, []);

  return (
    <AuthGate>
      {(session) => (
        activeDoc ? (
          <App
            documentId={activeDoc.id}
            documentName={activeDoc.name}
            onBackToList={handleBackToList}
          />
        ) : (
          <DocumentList
            onSelect={handleSelect}
            onLogout={handleLogout}
            userName={session.user.name || session.user.email}
          />
        )
      )}
    </AuthGate>
  );
}

// Public share links bypass auth entirely
const pathname = window.location.pathname;
const shareMatch = pathname.match(/^\/s\/([a-f0-9]+)$/);
const isResetPath = pathname === "/auth/reset" || pathname.startsWith("/auth/reset/");

function renderRoute() {
  if (shareMatch) return <ShareViewer token={shareMatch[1]} />;
  if (isResetPath) return <AuthReset />;
  return <Root />;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ModalProvider>
      {renderRoute()}
      <ToastContainer />
    </ModalProvider>
  </React.StrictMode>
);
